import { computed, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { type GetIntegrationDto, IntegrationProvider, IntegrationsService, IntegrationStatus } from '@api/uc-monitoring';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { NotificationsService } from '@ui';
import { catchError, map, of, Subject, switchMap } from 'rxjs';

export type UcMonitoringStatus = 'connected' | 'disabled' | 'error' | 'loading' | 'notConnected';

export function resolveUcMonitoringStatus(integrations: GetIntegrationDto[] | null | undefined) {
  const teams = integrations?.find(({ provider }) => provider === IntegrationProvider['MS-TEAMS']);

  if (!teams || teams.status === IntegrationStatus.DISABLEDBYSUPERADMIN) {
    return 'disabled';
  }

  return teams.status === IntegrationStatus.CONNECTED ? 'connected' : 'notConnected';
}

export const UcMonitoringStateStore = signalStore(
  { providedIn: 'root' },
  withState<{ status: UcMonitoringStatus }>({ status: 'loading' }),
  withComputed(({ status }) => ({
    isDisabled: computed(() => status() === 'disabled'),
    isEnabled: computed(() => {
      const current = status();

      return current === 'connected' || current === 'notConnected';
    }),
    isNotConnected: computed(() => status() === 'notConnected'),
    isReady: computed(() => status() !== 'loading' && status() !== 'notConnected'),
  })),
  withMethods((
    store,
    destroyRef = inject(DestroyRef),
    integrationsService = inject(IntegrationsService),
    notificationService = inject(NotificationsService),
  ) => {
    const loadTrigger$ = new Subject<void>();
    let isRequested = false;

    loadTrigger$.pipe(
      switchMap(() => integrationsService.getIntegrations({}).pipe(
        map(({ data }) => resolveUcMonitoringStatus(data)),
        catchError(error => {
          notificationService.showErrorMessage(`Failed to fetch UC Monitoring status: ${error.message}`);

          return of('error' as const);
        }),
      )),
      takeUntilDestroyed(destroyRef),
    ).subscribe(status => patchState(store, { status }));

    return {
      load() {
        if (isRequested) {
          return;
        }

        isRequested = true;
        loadTrigger$.next();
      },
      reload() {
        isRequested = true;
        patchState(store, {
          status: 'loading',
        });
        loadTrigger$.next();
      },
    };
  }),
);
